// Yup Validation
import * as yup from "yup";

const getEmailValidation = (t) =>
  yup
    .string()
    .email(t("userValidation.emailAddress"))
    .required(t("userValidation.emailRequired"));

const getPasswordValidation = (t) =>
  yup
    .string()
    .required(t("userValidation.passwordRequired"))
    .min(8, t("userValidation.passwordMinChar"))
    .test(
      "password-strength",
      t("userValidation.passwordStrength"),
      (value) => {
        return (
          /[a-z]/.test(value) && // lowercase letters
          /[A-Z]/.test(value) && // uppercase letters
          /(?:\d|[^A-Za-z0-9\s])/.test(value) // numbers and special characters
        );
      }
    );

const getCodeValidation = (t) =>
  yup
    .string()
    .required(t("userValidation.codeRequired"))
    .length(6, t("userValidation.codeMustBeSixDigits"));

const getConfirmPasswordValidation = (t, passwordField = "password") =>
  yup
    .string()
    .required(t("userValidation.confirmPasswordRequired"))
    .oneOf(
      [yup.ref(passwordField), null],
      t("userValidation.passwordsMustMatch")
    );

// ------------------ SIGN UP ------------------
export const signupValidationSchema = (t) =>
  yup.object({
    email: getEmailValidation(t),
    password: getPasswordValidation(t),
    confirmPassword: getConfirmPasswordValidation(t),
    termsAndConditions: yup
      .boolean()
      .oneOf([true], t("userValidation.termsAndConditions")),
  });

// ------------------ SIGN IN ------------------
export const signinValidationSchema = (t) =>
  yup.object({
    email: getEmailValidation(t),
    password: yup.string().required(t("userValidation.passwordRequired")),
  });

// -------------- RESET PASSWORD ---------------
export const emailValidationSchema = (t) =>
  yup.object({
    email: getEmailValidation(t),
  });

export const passwordValidationSchema = (t) =>
  yup.object({
    code: getCodeValidation(t),
    password: getPasswordValidation(t),
    confirmPassword: getConfirmPasswordValidation(t),
  });

export const changePasswordValidationSchema = (t) =>
  yup.object({
    oldPassword: yup.string().required(t("userValidation.passwordRequired")),
    password: getPasswordValidation(t),
    confirmPassword: getConfirmPasswordValidation(t),
  });

// ------------------ MBS AUTH ------------------
export const mbsSignInValidationSchema = (t) =>
  yup.object({
    userEmail: getEmailValidation(t),
    password: yup.string().required(t("userValidation.passwordRequired")),
  });

export const mbsSsoValidationSchema = (t) =>
  yup.object({
    userEmail: getEmailValidation(t),
  });

export const mbsForgotPasswordValidationSchema = (
  t,
  { isCodeStep = false } = {}
) =>
  yup.object({
    userEmail: getEmailValidation(t),
    ...(isCodeStep
      ? {
          code: getCodeValidation(t),
          newPassword: getPasswordValidation(t),
          confirmNewPassword: getConfirmPasswordValidation(t, "newPassword"),
        }
      : {}),
  });

export const mbsNewPasswordValidationSchema = (
  t,
  { requiresNameFields = false, temporaryPassword = "" } = {}
) =>
  yup.object({
    userFirstName: yup.string().when([], {
      is: () => requiresNameFields,
      then: (schema) =>
        schema.trim().required(t("mbsAuth.errors.firstNameRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    userLastName: yup.string().when([], {
      is: () => requiresNameFields,
      then: (schema) =>
        schema.trim().required(t("mbsAuth.errors.lastNameRequired")),
      otherwise: (schema) => schema.notRequired(),
    }),
    newPassword: getPasswordValidation(t).test(
      "different-from-temporary-password",
      t("mbsAuth.errors.newPasswordMustDifferFromTemporary"),
      (value) => {
        if (!temporaryPassword || !value) return true;
        return value !== temporaryPassword;
      }
    ),
    confirmNewPassword: getConfirmPasswordValidation(t, "newPassword"),
  });
